import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Cookies from 'js-cookie';

import BottomBar from './BottomBar';
import SeatSelectionForm from './SeatSelectionForm';

import AppHeader from '../components/AppHeader';
import Modal from '../components/Modal';
import SuccessMessages from '../components/SuccessMessages';

import Calendar from '../../../react/components/Calendar';
import Loader from '../../../../web/images/svg/loader.svg';

import { setCyoType, setCalendarView, updatePromoCode } from '../redux/sessionSlice';
import { useCartOverviewQuery, useSelectSeatsMutation } from '../redux/tessituraApiSlice';
import { getFixedCachedKey, tnewCookieKey } from '../utils';
import { useAppDispatch, useAppSelector } from '../hooks';

const App = ({ node }: AppProps) => {
  const cacheKey = 'select-seats';
  const {
    checkoutUrl,
    intro,
    instructions,
    cyoType,
  } = node.dataset;

  const [hasCriticalError, setHasCriticalError] = useState(false);
  window.addEventListener('sessionIsInactive', () => setHasCriticalError(true));

  const [skipCartOverview, setSkipCartOverview] = useState(true);
  window.addEventListener('getMetaDataCalled', () => setSkipCartOverview(false));

  const dispatch = useAppDispatch();

  const [hasResetableError, setHasResetableError] = useState(false);
  const [hasCookie, setHasCookie] = useState(!!Cookies.get(tnewCookieKey));

  const [modal, setModal] = useState(<></>);

  const [selectSeats, { data }] = useSelectSeatsMutation({ fixedCacheKey: getFixedCachedKey(cacheKey) });

  const {
    data: cartOverview,
    isLoading: isGettingCartOverview,
  } = useCartOverviewQuery(null, { skip: skipCartOverview });

  const {
    activePromoCode,
    currentErrorCode,
    currentErrorMessage,
    packageLineNo,
    promoCode,
    seats,
    showCalendar,
    specialRequests,
    successMessages,
  } = useAppSelector((state) => state.sessionSlice);

  const handleApplyForm = useCallback(async () => {
    try {
      await selectSeats({
        promoCode,
        seats,
        specialRequests,
      });

      dispatch(setCalendarView(true));

      // on reload, we can't get the actual promo code, but we do know if a promo code is applied
      if (activePromoCode && !promoCode) {
        dispatch(updatePromoCode('applied'));
      }
    } catch ({ error }: any) { // eslint-disable-line @typescript-eslint/no-explicit-any
      throw new Error(`Error selecting seats: ${error}`);
    }
  }, [activePromoCode, dispatch, promoCode, seats, selectSeats, specialRequests]);

  useEffect(() => {
    dispatch(setCyoType(cyoType));
  }, [cyoType, dispatch]);

  useEffect(() => {
    // we need getMetaData call to finish and the TNEW cookiue created before we do anything
    if (!hasCookie) {
      const cookieCheck = setInterval(() => {
        setHasCookie(!!Cookies.get(tnewCookieKey));
      }, 100);

      return () => clearInterval(cookieCheck);
    }

    return undefined;
  }, [hasCookie]);

  useEffect(() => {
    switch (currentErrorCode) {
      case '401':
        setHasResetableError(true);

        break;
      case '500':
        setHasCriticalError(true);

        break;
      default:
        break;
    }
  }, [currentErrorCode]);

  useEffect(() => {
    if (!data && packageLineNo && seats) handleApplyForm();
  }, [data, handleApplyForm, packageLineNo, seats]);

  if (hasCriticalError || hasResetableError) {
    document.body.classList.add('has-modal-opened');

    return (
      <Modal
        text={currentErrorMessage ? <p>{currentErrorMessage}</p> : null}
        type={hasCriticalError ? 'critical-error' : 'resetable-error'}
        checkoutUrl={checkoutUrl}
      />
    );
  }

  if (!hasCookie || isGettingCartOverview || !cartOverview) return <div className="c-loader c-loader--cyop"><Loader /></div>;

  return (
    <>
      {successMessages && <SuccessMessages message={successMessages} />}
      <AppHeader intro={intro} instructions={instructions} />
      <SeatSelectionForm handleApplyForm={handleApplyForm} setModal={setModal} />
      {showCalendar && <Calendar cyop setModal={setModal} />}
      {!hasCriticalError && modal}
      <BottomBar checkoutUrl={checkoutUrl || 'https://syso-tnew-test.tnhs.cloud/cart/details'} />
    </>
  );
};

const appPropTypes = {
  node: PropTypes.instanceOf(HTMLElement).isRequired,
};

App.propTypes = appPropTypes;

type AppProps = PropTypes.InferProps<typeof appPropTypes>;

export default App;
